import { Loading } from "./brand";
import "@fontsource-variable/schibsted-grotesk";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "./styles.css";

import { Code, ConnectError } from "@connectrpc/connect";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Component, StrictMode, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { browsable, ConnectionProvider, useConnection } from "./connection";
import { router } from "./router";
import { lazy, Suspense } from "react";

// Signed-in visits, most of them, never load the sign-in screen.
const SignInView = lazy(() => import("./views/SignIn").then((m) => ({ default: m.SignInView })));

/**
 * Signed out: the sign-in screen. Signed in: the app, with a fresh cache per session so two
 * people's data never mix. The provider is remounted by key: a memo would not do, because
 * nothing inside it reads the key and the React Compiler is free to keep one cache for ever.
 */
function App() {
  const { target, signedIn } = useConnection();
  if (!signedIn && !browsable(location.pathname))
    return (
      <Suspense fallback={<Loading />}>
        <SignInView />
      </Suspense>
    );
  return <Scoped key={target.token ?? ""} />;
}

/** Retry what may pass on a second try; a refused token or a missing thing will not. */
const final = [Code.Unauthenticated, Code.PermissionDenied, Code.NotFound, Code.InvalidArgument];
const retry = (n: number, e: unknown) => n < 2 && !final.includes(ConnectError.from(e).code);

function Scoped() {
  const [client] = useState(() => new QueryClient({ defaultOptions: { queries: { retry, refetchOnWindowFocus: false } } }));
  return (
    <QueryClientProvider client={client}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

/** Last resort for an error outside any view: a page that explains and reloads, never a blank screen. */
class Crashed extends Component<{ children: ReactNode }, { error?: unknown }> {
  state: { error?: unknown } = {};
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="state" role="alert" style={{ marginTop: "20vh" }}>
        <h2>Grovedock hit an error</h2>
        <p>Reloading usually fixes it. If it keeps happening, write to support@grovedock.com.</p>
        <p>
          <button type="button" className="button" data-primary="" onClick={() => location.reload()}>
            Reload
          </button>
        </p>
      </main>
    );
  }
}

// After a deploy, a tab opened before it asks for screens by their old file names, which are
// gone: load the new version once, rather than show an error. Not twice in a minute, in case the
// file is missing for another reason.
window.addEventListener("vite:preloadError", (e) => {
  try {
    const last = Number(sessionStorage.getItem("gd-reloaded-at") ?? 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem("gd-reloaded-at", String(Date.now()));
  } catch {
    return;
  }
  e.preventDefault();
  location.reload();
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Crashed>
      <ConnectionProvider>
        <App />
      </ConnectionProvider>
    </Crashed>
  </StrictMode>,
);
